import React from 'react';
import { atom, useRecoilState } from 'recoil';

import { usePrevious, useScrollListener } from '@gitbook/util-react-hooks';

import { useNavigationState } from '../../../../navigation';
import type { ScrollRef } from './PageSheetView';

export const PAGE_SCROLL_POSITION_ATOM = 'pageScrollPositionAtom';

interface PagesScrollPositionState {
    [key: string]: {
        top: number;
        left: number;
    };
}
const defaultState: PagesScrollPositionState = {};

export const pageScrollPositionAtom = atom<PagesScrollPositionState>({
    key: PAGE_SCROLL_POSITION_ATOM,
    default: defaultState,
});

/**
 * Hook to store the scroll position of a page.
 *
 * We listen to and save the scroll position of an editor page
 * so that we can restore it when navigating back to the page (see packages/react-app/src/Screens/Space/Page/useInitialScrollPage.ts).
 */
export function useStorePageScrollPosition(scrollRef: ScrollRef, pageId?: string) {
    const navigationState = useNavigationState();
    const [pagesScrollState, setPagesScrollState] = useRecoilState(pageScrollPositionAtom);
    const storedScrollStateRef = React.useRef(pageId ? pagesScrollState[pageId] : undefined);
    React.useEffect(() => {
        storedScrollStateRef.current = pageId ? pagesScrollState[pageId] : undefined;
    }, [pageId, pagesScrollState]);

    const scrollPosition = React.useRef<{ top: number; left: number }>({
        top: 0,
        left: 0,
    });

    useScrollListener(
        () => {
            if (!scrollRef?.current) {
                return;
            }
            const { scrollTop, scrollLeft } = scrollRef.current;
            scrollPosition.current = {
                top: scrollTop,
                left: scrollLeft,
            };
        },
        scrollRef,
        { useScrollEnd: true }
    );

    React.useLayoutEffect(() => {
        // if new page has no stored scroll state, we initialize it at top of the page.
        if (!storedScrollStateRef.current && pageId) {
            setPagesScrollState((prevState) => ({
                ...prevState,
                [pageId]: {
                    top: 0,
                    left: 0,
                },
            }));
        }

        return () => {
            // When the page component unmounts or screen state changes,
            // we save the scroll position (if it has changed)
            // so that we can restore it when navigating back to this page.
            if (scrollPosition.current && pageId) {
                const { top, left } = scrollPosition.current;
                const hasChanged =
                    storedScrollStateRef.current?.top !== top ||
                    storedScrollStateRef.current?.left !== left;
                if (hasChanged) {
                    setPagesScrollState((prevState) => ({
                        ...prevState,
                        [pageId]: {
                            top,
                            left,
                        },
                    }));
                }
            }
        };
    }, [pageId, setPagesScrollState]);

    const hadSideSheet = usePrevious(
        navigationState.screen?.type === 'space' && navigationState.screen?.sideSheet
    );
    const hasSideSheet =
        navigationState.screen?.type === 'space' && navigationState.screen?.sideSheet;

    const pageIdRef = React.useRef(pageId);
    React.useEffect(() => {
        pageIdRef.current = pageId;
    }, [pageId]);

    React.useLayoutEffect(() => {
        const sideSheetChanged = hadSideSheet !== hasSideSheet;
        // store scroll position when side sheet state changes
        // so we can maintain the correct scroll position when the side sheet opens or closes.
        if (sideSheetChanged && scrollPosition.current) {
            const { top, left } = scrollPosition.current;
            const id = pageIdRef.current;
            if (!id) {
                return;
            }
            const hasChanged =
                storedScrollStateRef.current?.top !== top ||
                storedScrollStateRef.current?.left !== left;
            if (hasChanged) {
                setPagesScrollState((prevState) => ({
                    ...prevState,
                    [id]: {
                        top,
                        left,
                    },
                }));
            }
        }
    }, [hadSideSheet, hasSideSheet, setPagesScrollState]);

    return {
        pageScrollPosition: React.useMemo(() => {
            const scrollPosition = pageId ? pagesScrollState[pageId] : { top: 0, left: 0 };
            return scrollPosition
                ? {
                      top: scrollPosition.top,
                      left: scrollPosition.left,
                  }
                : undefined;
        }, [pagesScrollState, pageId]),
    };
}
